import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  CircularProgress, Alert, Chip, Divider, InputAdornment,
  IconButton,
} from '@mui/material';
import { Phone, Lock, Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material';
import axios from 'axios';
import { Config } from '../config/Config';

const api = axios.create({ timeout: 15000 });

export default function DeleteAccount() {
  // Step 1 — search by phone
  const [phone, setPhone]         = useState('');
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [searchErr, setSearchErr] = useState('');

  // Step 2 — confirm with password
  const [password, setPassword]   = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [deleted, setDeleted]     = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchErr('');
    setFoundUser(null);
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length !== 10) { setSearchErr('Please enter a valid 10-digit phone number.'); return; }
    setSearching(true);
    try {
      const res = await api.post(Config.base_url + '/account/lookup', { phone: cleaned });
      setFoundUser(res.data?.user || null);
    } catch (err) {
      setSearchErr(err.response?.data?.message || 'No account found with this phone number.');
    } finally {
      setSearching(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleteErr('');
    if (!password) { setDeleteErr('Please enter your password.'); return; }
    setDeleting(true);
    try {
      await api.post(Config.base_url + '/account/delete', {
        user_id:  foundUser.id,
        phone:    phone.replace(/\D/g, ''),
        password: password,
      });
      setDeleted(true);
    } catch (err) {
      setDeleteErr(err.response?.data?.message || 'Verification failed. Please check your password.');
    } finally {
      setDeleting(false);
    }
  };

  const reset = () => {
    setPhone(''); setFoundUser(null); setSearchErr('');
    setPassword(''); setDeleted(false); setDeleteErr('');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0A2540 0%, #0d47a1 60%, #1565c0 100%)',
      p: 2,
    }}>
      <Card sx={{ maxWidth: 460, width: '100%', borderRadius: 3, boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%',
              bgcolor: '#fff1f2', border: '2px solid #fecdd3',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
            }}>
              <Cancel sx={{ fontSize: 30, color: '#dc2626' }} />
            </Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a">Delete Account</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Enter your phone number to find your account, then confirm with your password.
            </Typography>
          </Box>

          {/* ── SUCCESS STATE ── */}
          {deleted ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 56, color: '#16a34a', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} color="#15803d">Account Deleted</Typography>
              <Typography variant="body2" color="text.secondary" mt={1} mb={3}>
                Your account has been successfully deactivated.
              </Typography>
              <Button onClick={reset} variant="outlined" sx={{ borderRadius: 2 }}>Start Over</Button>
            </Box>
          ) : (
            <>
              {/* ── STEP 1: Search by phone ── */}
              <form onSubmit={handleSearch}>
                <Typography variant="body2" fontWeight={700} color="#0f172a" mb={1}>
                  Step 1 — Find your account
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number (10 digits)"
                  value={phone}
                  onChange={e => {
                    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setFoundUser(null);
                    setSearchErr('');
                  }}
                  sx={{ mb: 1.5 }}
                  inputProps={{ maxLength: 10 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment>,
                  }}
                />
                {searchErr && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: 13 }}>{searchErr}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={searching || phone.length !== 10}
                  sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#0A2540,#1565c0)', fontWeight: 700 }}
                >
                  {searching ? <CircularProgress size={20} color="inherit" /> : 'Find Account'}
                </Button>
              </form>

              {/* ── Found user info ── */}
              {foundUser && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Account card */}
                  <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Account Found
                    </Typography>
                    <Typography fontWeight={700} fontSize={15} mt={0.5}>{foundUser.name}</Typography>
                    {foundUser.masked_email && foundUser.masked_email !== '—' && (
                      <Typography fontSize={12} color="text.secondary">{foundUser.masked_email}</Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={foundUser.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={foundUser.is_active ? 'success' : 'default'}
                        sx={{ fontWeight: 600, fontSize: 10 }}
                      />
                      <Chip
                        label={`KYC: ${(foundUser.kyc_status || 'pending').toUpperCase()}`}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: 10 }}
                      />
                    </Box>
                    {!foundUser.is_active && (
                      <Alert severity="info" sx={{ mt: 1.5, py: 0.5, fontSize: 12 }}>
                        This account is already deactivated.
                      </Alert>
                    )}
                  </Box>

                  {/* ── STEP 2: Confirm with password ── */}
                  {foundUser.is_active && (
                    <form onSubmit={handleDelete}>
                      <Typography variant="body2" fontWeight={700} color="#0f172a" mb={1}>
                        Step 2 — Confirm with your password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="Password"
                        type={showPass ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPass(p => !p)}>
                                {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {deleteErr && <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: 13 }}>{deleteErr}</Alert>}
                      <Alert severity="warning" sx={{ mb: 2, py: 0.5, fontSize: 12 }}>
                        This will permanently deactivate your account and cannot be undone.
                      </Alert>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="error"
                        disabled={deleting || !password}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      >
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete My Account'}
                      </Button>
                    </form>
                  )}
                </>
              )}
            </>
          )}

        </CardContent>
      </Card>
    </Box>
  );
}
